import { useAccountAspect } from "@oysterhr/identity"
import { useCreateCustomerBankAccount } from "../../../../hooks/useCreateCustomerBankAccount"
import { useNewCompanyPaymentMethods } from "../../../../hooks/useNewCompanyPaymentMethods"
import { useUpdateCustomerBankAccount } from "../../../../hooks/useUpdateCustomerBankAccount"
import { useCreateStripeAch } from "./useCreateStripeAch"
import { useConfiguredToasts } from "@oysterhr/feature-components"
import { type AddAchFormValues } from "../../../types"
import {
  CountryIsoCode,
  CurrencyEnum,
  PaymentMethodsCustomerBankAccountEnumType,
} from "@oysterhr/schema"
import { useStripe } from "@stripe/react-stripe-js"
import { getStripeToken } from "../utils/formHelpers"
import { ACH_SKIP_PLAID_VERIFICATION_REASON_KEY } from "../../../constants"

export const useUpsertAchPaymentMethod = ({ onSuccess }: { onSuccess: () => void }) => {
  const { accountAspect: companyId } = useAccountAspect()
  const { displayErrorToast } = useConfiguredToasts()
  const stripe = useStripe()
  const stripeReady = !!stripe

  // GraphQL
  const [createAchPaymentMethod, { loading: creatingAchPaymentMethod }] = useCreateStripeAch({
    analyticsEventName: "stripe_ach_payment_method_created",
  })
  const [updateCustomerBankAccount, { loading: updatingCustomerBankAccount }] =
    useUpdateCustomerBankAccount()
  const [createCustomerBankAccount, { loading: creatingCustomerBankAccount }] =
    useCreateCustomerBankAccount()
  const { data: companyPaymentMethodsData } = useNewCompanyPaymentMethods({
    variables: { companyId },
    fetchPolicy: "cache-and-network",
  })

  const customerBankAccountId = companyPaymentMethodsData?.company.customerBankAccount?.id
  const isLoading =
    creatingCustomerBankAccount || updatingCustomerBankAccount || creatingAchPaymentMethod

  // Actions
  const createFTEPaymentMethod = (values: AddAchFormValues) => {
    return createCustomerBankAccount({
      variables: {
        ownerId: companyId,
        paymentMethodType: PaymentMethodsCustomerBankAccountEnumType.Ach,
        billingAddress: null,
        userNotSigner: null,
        bankAccountType: values.bankAccountType,
        bankDetails: {
          ach: {
            accountHolderName: values.accountHolderName,
            currencyCode: values.currencyCode as CurrencyEnum,
            countryCode: values.country as CountryIsoCode,
            accountNumber: values.accountNumber,
            achRoutingNumber: values.routingNumber,
          },
        },
      },
      onCompleted: data => {
        if (data.createCustomerBankAccount.errors.length) {
          displayErrorToast({
            content: data?.createCustomerBankAccount?.errors.join(", "),
          })
        } else {
          onSuccess()
        }
      },
    })
  }

  const updateFTEPaymentMethod = async (
    customerBankAccountId: string,
    values: AddAchFormValues,
  ) => {
    return await updateCustomerBankAccount({
      variables: {
        customerBankAccountId,
        userNotSigner: null,
        billingAddress: null,
        bankAccountType: values.bankAccountType,
        bankDetails: {
          ach: {
            accountHolderName: values.accountHolderName,
            currencyCode: values.currencyCode as CurrencyEnum,
            countryCode: values.country as CountryIsoCode,
            accountNumber: values.accountNumber,
            achRoutingNumber: values.routingNumber,
          },
        },
      },
      onCompleted: data => {
        if (data.updateCustomerBankAccount.errors.length) {
          displayErrorToast({
            content: data?.updateCustomerBankAccount?.errors.join(", "),
          })
        } else {
          onSuccess()
        }
      },
    })
  }

  const createPaymentMethod = async (values: AddAchFormValues) => {
    try {
      const token = await getStripeToken(stripe, values)
      const reasonForNotUsingPlaid =
        localStorage.getItem(ACH_SKIP_PLAID_VERIFICATION_REASON_KEY) || ""

      return await createAchPaymentMethod({
        variables: {
          ownerId: companyId,
          accountNumber: values.accountNumber,
          routingNumber: values.routingNumber,
          accountHolderName: values.accountHolderName,
          stripeBankToken: token.id,
          bankAccountType: values.bankAccountType,
          reasonForNotUsingPlaid: reasonForNotUsingPlaid,
        },
        onCompleted: data => {
          if (data.createPaymentMethodStripeAch.errors.length) {
            throw new Error(data.createPaymentMethodStripeAch.errors.join(", "))
          } else {
            onSuccess()
          }
        },
      })
    } catch (err) {
      displayErrorToast({
        content: (err as Error).message,
      })
      console.error(err)
      throw err
    }
  }

  const upsertFTEPaymentMethod = (values: AddAchFormValues) => {
    return customerBankAccountId
      ? updateFTEPaymentMethod(customerBankAccountId, values)
      : createFTEPaymentMethod(values)
  }

  return {
    upsertFTEPaymentMethod,
    createPaymentMethod,
    stripeReady,
    isLoading,
  }
}
